import { ContentBox } from "@msuite/ui/app-shell";
import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { LoadingScreen } from "@msuite/ui/loading-screen";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { Tabs } from "@msuite/ui/tabs";
import { responseHandler } from "@msuite/ui/utils";
import React from "react";
import { useParams } from "react-router";
import { ContactHeader } from "@/components/contacts";
import { client } from "@/hono";
import { EditContactModal } from "@/modals/contacts/edit-contact-modal";
import { ContactBankAccounts } from "./contact-bank-accounts";
import { ContactGeneral } from "./contact-general";
import { ContactMembers } from "./contact-members";
import { ContactProjects } from "./contact-projects";
import { ContactServices } from "./contact-services";
import { useHiddenContactTabs } from "@/hooks/use-contact-tabs";
import { Can } from "@/components/citadel";
import { contactTabCyId } from "./tabs";

export function Contact() {
	/** Hooks */
	const params = useParams();
	const contactId = params.contactId;
	const { t } = useTranslation();
	const editContactModal = useDisclosure();
	const hiddenTabs = useHiddenContactTabs();

	/** Query */
	const {
		data: contact,
		isLoading,
		isError,
		refetch,
	} = useQuery({
		queryKey: ["contact", params.contactId ?? ""],
		queryFn: async () => {
			if (!params.contactId) throw new Error("Contact ID is required");
			const response = await client.contacts[":contactId"].$get({
				param: {
					contactId: params.contactId,
				},
			});
			const data = await responseHandler(response);
			const contact = data.contact;
			return contact;
		},
	});

	if (!contactId) return <BlankSlate />;
	if (isLoading) return <LoadingScreen />;
	if (isError) return <BlankSlate />;
	if (!contact) return <BlankSlate />;

	/** Render */
	return (
		<React.Fragment>
			<ContentBox bottomPadding="none">
				<ContactHeader contact={contact} />
				<Tabs
					panelKey="contact-tabs"
					header={() => (
						<Can
							action="update"
							subject="Contact"
						>
							<Button
								icon={<Icon name="Edit" />}
								onClick={editContactModal.onOpen}
								variant="primary"
							>
								{t("general.edit")}
							</Button>
						</Can>
					)}
					groups={[
						{
							name: "general",
							panels: [
								{
									label: t("general.general"),
									panel: () => <ContactGeneral contactId={contactId} />,
									errors: null,
									cyId: contactTabCyId("general"),
								},
							],
						},
						{
							name: "content",
							panels: [
								hiddenTabs.includes("projects")
									? null
									: {
											label: t("projects.projects"),
											panel: () => <ContactProjects contactId={contactId} />,
											errors: null,
											cyId: contactTabCyId("projects"),
										},
								{
									label: t("contacts.members"),
									panel: () => <ContactMembers contactId={contactId} />,
									errors: null,
									cyId: contactTabCyId("members"),
								},
								hiddenTabs.includes("services")
									? null
									: {
											label: t("organization.data.services"),
											panel: () => <ContactServices contactId={contactId} />,
											errors: null,
											cyId: contactTabCyId("services"),
										},
								{
									label: t("contacts.bank_accounts.bank_accounts"),
									panel: () => <ContactBankAccounts contactId={contactId} />,
									errors: null,
									cyId: contactTabCyId("bank-accounts"),
								},
							],
						},
					]}
				/>
			</ContentBox>
			{editContactModal.isOpen && (
				<EditContactModal
					isOpen={editContactModal.isOpen}
					onClose={editContactModal.onClose}
					contactId={contactId}
					onComplete={refetch}
				/>
			)}
		</React.Fragment>
	);
}
